import CoverageExtraInputsPanel from "./CoverageExtraInputsPanel";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import { Box, IconButton, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useElementSize from "common/hooks/useElementSize";
import * as logger from "common/logger";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as programPricingResolver from "domain/programPricingResolver";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programPricingStore from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";

const DEFAULT_DEDUCTIBLE_APPLICATION_OPTS = [
  "Policy",
  "Location",
  "LOC", // Line of Coverage
  "Building",
];

const resolveChoices = (peril) =>
  peril === "__ALL__" ? ["Policy"] : DEFAULT_DEDUCTIBLE_APPLICATION_OPTS;

const makeInputBasedCellClassName = (params) => {
  const value = params?.value ?? null;
  const inputValue = params?.row?.[`__input__${params?.field}`] ?? null;
  if (inputValue == null) {
    return "defaulted";
  } else if (inputValue === value) {
    return "user-input";
  } else {
    return "user-input-override";
  }
};

const COLUMN_DEF_MAP = {
  label: {
    field: "label",
    headerName: "Coverage Type",
    headerAlign: "center",
    editable: false,
    // Unsortable regardless of config: expanded extra-input detail rows pair
    // with their coverage row by adjacency, which sorting would break — and
    // sortability must not silently change when a config adds extra inputs.
    sortable: false,
    width: 200,
  },
  sublimits: makePrettyNumberColDef({
    field: "sublimits",
    headerName: "$",
    headerAlign: "center",
    prefix: "$",
    width: 200,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  expiringSublimits: makePrettyNumberColDef({
    field: "expiringSublimits",
    headerName: "Expiring",
    headerAlign: "center",
    prefix: "$",
    width: 200,
    sortable: false,
    editable: false,
    cellClassName: "not-editable",
  }),
  deductibles: makePrettyNumberColDef({
    field: "deductibles",
    headerName: "$",
    headerAlign: "center",
    prefix: "$",
    width: 100,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  deductiblePercentages: makePrettyNumberColDef({
    field: "deductiblePercentages",
    headerName: "%",
    headerAlign: "center",
    suffix: "%",
    width: 100,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  deductibleMins: makePrettyNumberColDef({
    field: "deductibleMins",
    headerName: "Min",
    headerAlign: "center",
    width: 100,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  deductibleMaxs: makePrettyNumberColDef({
    field: "deductibleMaxs",
    headerName: "Max",
    headerAlign: "center",
    width: 100,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  deductibleApplications: {
    field: "deductibleApplications",
    headerName: "Application",
    headerAlign: "center",
    type: "singleSelect",
    valueOptions: ({ row }) => row?.deductibleApplicationOptions ?? [],
    width: 150,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  },
  debitCredit: makePrettyNumberColDef({
    field: "debitCredit",
    headerName: "Debit/Credit",
    headerAlign: "center",
    suffix: "%",
    width: 150,
    sortable: false,
    editable: true,
    cellClassName: makeInputBasedCellClassName,
  }),
  coverageNotes: {
    field: "coverageNotes",
    headerName: "Notes",
    headerAlign: "center",
    type: "text",
    width: 300,
    sortable: false,
    editable: true,
  },
};

const DEFAULT_COVERAGE_COLUMNS = [
  "label",
  "sublimits",
  "expiringSublimits",
  "deductibles",
  "deductiblePercentages",
  "deductibleMins",
  "deductibleMaxs",
  "coverageNotes",
];

const makeColumns = (configColumns) => {
  const columnKeys = configColumns ?? DEFAULT_COVERAGE_COLUMNS;
  return columnKeys.map((key) => COLUMN_DEF_MAP[key]).filter(Boolean);
};

const useCoverages = (perils) => {
  const programPricing = useSelector(programPricingStore.select);
  const programPricingInput = useSelector(programPricingStore.selectInput);

  const priorState = useSelector(stateSelectors.selectPriorState);
  const priorSOV = priorState?.SOV ?? null;
  const priorResolvedLayers = useMemo(
    () => pricingSelectors.selectResolvedLayers(priorState),
    [priorState]
  );
  const priorResolvedProgramPricing = useMemo(() => {
    if (!priorSOV) return null;
    return (
      programPricingResolver.resolve(priorState?.programPricing, {
        layers: priorResolvedLayers,
        SOV: priorSOV,
      }) ?? null
    );
  }, [priorState, priorResolvedLayers, priorSOV]);
  const priorSublimits = priorResolvedProgramPricing?.sublimits ?? {};

  const fields = [
    "sublimits",
    "deductibles",
    "deductiblePercentages",
    "deductibleMins",
    "deductibleMaxs",
    "deductibleApplications",
    "debitCredit",
    "coverageNotes",
  ];

  const perilsConfig = programPricing?.coverageConfig?.perils;

  const extraInputsByPeril = useMemo(
    () => filterExtraInputsByPeril(perilsConfig),
    [perilsConfig]
  );

  const coverages = (perils ?? []).map((peril) => ({
    id: peril,
    label: perilsConfig?.[peril]?.name ?? peril,
    deductibleApplicationOptions: resolveChoices(
      perilsConfig?.[peril]?.debitApplication
    ),
    ...Object.fromEntries(
      fields.map((field) => [field, programPricing?.[field]?.[peril] ?? null])
    ),
    ...Object.fromEntries(
      fields.map((field) => [
        `__input__${field}`,
        programPricingInput?.[field]?.[peril] ?? null,
      ])
    ),
    expiringSublimits: priorSublimits?.[peril] ?? null,
  }));

  return { coverages, extraInputsByPeril, hasPriorData: !!priorSOV };
};

const SUBLIMIT_FIELDS = new Set(["sublimits", "expiringSublimits"]);
const DEDUCTIBLE_FIELDS = new Set([
  "deductibles",
  "deductiblePercentages",
  "deductibleMins",
  "deductibleMaxs",
  "deductibleApplications",
]);

const buildColumnGroupingModel = (columnKeys) => {
  const groups = [];

  const sublimitChildren = columnKeys
    .filter((k) => SUBLIMIT_FIELDS.has(k))
    .map((field) => ({ field }));
  if (sublimitChildren.length > 0) {
    groups.push({
      groupId: "sublimits",
      headerName: "Sublimits",
      headerAlign: "center",
      children: sublimitChildren,
    });
  }

  const deductibleChildren = columnKeys
    .filter((k) => DEDUCTIBLE_FIELDS.has(k))
    .map((field) => ({ field }));
  if (deductibleChildren.length > 0) {
    groups.push({
      groupId: "deductibles",
      headerName: "Deductibles",
      headerAlign: "center",
      children: deductibleChildren,
    });
  }

  return groups;
};

// Extra-input definitions for the coverage grids live on the peril
// definitions in the "submission_defaults" config, at
// programPricing.coverageConfig.perils[<peril>].extraInputs — an optional
// list of the extra inputs shown when that peril's row is expanded. Because
// coverageConfig is copied into each submission's programPricing at creation
// (and refreshed by the reset button on Cat Coverages), the definitions are
// a per-submission snapshot: config changes only reach existing submissions
// on reset. A peril listed in both coverage grids shows the same extra
// inputs in both. Rows whose peril has no extraInputs render without an
// expand chevron. Each entry is a definition consumed by
// components/common/ExtraInput, e.g.:
//
//   "perils": {
//     "__ALL__": {
//       "name": "Program",
//       "extraInputs": [
//         {
//           "key": "coverageCommentary",
//           "type": "text",
//           "label": "Commentary",
//           "textFieldProps": { "multiline": true, "minRows": 3, "fullWidth": true }
//         }
//       ]
//     },
//     "EQ": {
//       "name": "Earthquake",
//       "parent": "__ALL__",
//       "extraInputs": [
//         { "key": "deductibleBasis", "type": "text", "label": "Deductible Basis" },
//         { "key": "aalEstimate", "type": "number", "label": "AAL Estimate" }
//       ]
//     }
//   }
//
// Values are stored row-major under the single reserved programPricing field
// "extraInputs", at programPricing.extraInputs[<peril>][<key>], so document
// templates read them as S.programPricing.extraInputs.<peril>.<key>. Keeping
// everything inside that one namespace means config-defined keys can never
// collide with real programPricing fields, so no reserved-key list is needed.
// Definitions whose key is missing or not a string are dropped with a warning.
const filterExtraInputs = (rawExtraInputs) => {
  if (rawExtraInputs == null) return [];
  if (!Array.isArray(rawExtraInputs)) {
    logger.breadcrumb(
      "Dropping coverage extra-inputs config: expected a list of input definitions on the peril definition",
      { level: logger.LEVEL.WARNING }
    );
    return [];
  }
  return rawExtraInputs.filter((definition) => {
    const key = definition?.key;
    if (typeof key !== "string" || key.length === 0) {
      logger.breadcrumb(
        "Dropping coverage extra-input definition with missing or non-string key",
        { level: logger.LEVEL.WARNING }
      );
      return false;
    }
    return true;
  });
};

const filterExtraInputsByPeril = (perilsConfig) =>
  Object.fromEntries(
    Object.entries(perilsConfig ?? {})
      .map(([peril, definition]) => [
        peril,
        filterExtraInputs(definition?.extraInputs),
      ])
      .filter(([, definitions]) => definitions.length > 0)
  );

// Horizontal padding + borders of a .detail-panel-cell, so the sticky panel
// inside it ends level with the grid's visible right edge.
const DETAIL_PANEL_INSET_PX = 34;

const CoveragesGrid = ({ perils, columnsKey, disabled = false }) => {
  const dispatch = useDispatch();
  const {
    data: catExposuresConfig,
    isLoading: isConfigLoading,
  } = staticDataService.useConfigQuery("cat_exposures");
  const configColumns = catExposuresConfig?.[columnsKey];

  const [expandedPerils, setExpandedPerils] = useState(() => new Set());
  const toggleExpanded = useCallback(
    (perilId) =>
      setExpandedPerils((previous) => {
        const next = new Set(previous);
        if (next.has(perilId)) {
          next.delete(perilId);
        } else {
          next.add(perilId);
        }
        return next;
      }),
    []
  );

  const { coverages, extraInputsByPeril, hasPriorData } = useCoverages(perils);

  const hasExtraInputs = useMemo(
    () =>
      coverages.some(
        (coverage) => (extraInputsByPeril[coverage.id] ?? []).length > 0
      ),
    [coverages, extraInputsByPeril]
  );

  // Visible width of the grid's wrapper, so the detail panel can be capped
  // at the *visible* width rather than the full scrollable row width.
  const [containerRef, { width: containerWidth }] = useElementSize();

  const columns = useMemo(() => {
    const baseColumns = makeColumns(configColumns).map((column) =>
      disabled ? { ...column, editable: false } : column
    );
    if (!hasExtraInputs) {
      return baseColumns;
    }
    // The chevron renders inside the coverage-name cell (right-aligned)
    // rather than in a dedicated column, and the expanded detail panel spans
    // from that cell to the end of the row. Without a "label" column there is
    // nowhere safe to host the chevron (grafting it onto another column would
    // clobber that column's renderCell), so fail safe: plain grid, no expand.
    const chevronIndex = baseColumns.findIndex(
      (column) => column.field === "label"
    );
    if (chevronIndex === -1) {
      return baseColumns;
    }
    const detailSpan = baseColumns.length - chevronIndex;
    return baseColumns.map((column, index) =>
      index === chevronIndex
        ? {
            ...column,
            colSpan: (value, row) => (row.__detail ? detailSpan : 1),
            cellClassName: (params) =>
              params.row?.__detail ? "detail-panel-cell" : "",
            renderCell: (params) =>
              params.row?.__detail ? (
                // The detail cell spans the full scrollable row width; pin
                // the panel to the grid's visible width so its inputs wrap
                // at the container edge instead of running off-screen.
                <Box
                  sx={{
                    position: "sticky",
                    left: 0,
                    width: containerWidth
                      ? `${containerWidth - DETAIL_PANEL_INSET_PX}px`
                      : "100%",
                  }}
                >
                  <CoverageExtraInputsPanel
                    peril={params.row.peril}
                    definitions={extraInputsByPeril[params.row.peril]}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box
                    component={"span"}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {params.value}
                  </Box>
                  {(extraInputsByPeril[params.row.id] ?? []).length > 0 && (
                    <IconButton
                      size={"small"}
                      aria-label={
                        expandedPerils.has(params.row.id)
                          ? "collapse row"
                          : "expand row"
                      }
                      onClick={() => toggleExpanded(params.row.id)}
                    >
                      {expandedPerils.has(params.row.id) ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  )}
                </Box>
              ),
          }
        : column
    );
  }, [
    disabled,
    configColumns,
    hasExtraInputs,
    extraInputsByPeril,
    expandedPerils,
    toggleExpanded,
    containerWidth,
  ]);

  const rows = useMemo(
    () =>
      hasExtraInputs
        ? coverages.flatMap((coverage) =>
            expandedPerils.has(coverage.id) &&
            (extraInputsByPeril[coverage.id] ?? []).length > 0
              ? [
                  coverage,
                  {
                    id: `${coverage.id}__detail`,
                    __detail: true,
                    peril: coverage.id,
                  },
                ]
              : [coverage]
          )
        : coverages,
    [coverages, hasExtraInputs, expandedPerils, extraInputsByPeril]
  );

  const columnGroupingModel = useMemo(
    () => buildColumnGroupingModel(configColumns ?? DEFAULT_COVERAGE_COLUMNS),
    [configColumns]
  );

  const theme = useTheme();
  const borderColor = theme.brand.gray.gainsboro;

  if (isConfigLoading || !coverages.length) return null;

  const updateCoverage = ({ peril, field, value }) => {
    setTimeout(
      () =>
        dispatch(
          programPricingStore.updatePathValue({
            path: [field, peril],
            value: value,
          })
        ),
      0
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        // The columns have fixed pixel widths that can exceed the parent
        // card; cap at the container and let the DataGrid scroll
        // horizontally instead of overflowing it.
        width: "fit-content",
        maxWidth: "100%",
        "& .not-editable": {
          bgcolor: "#EFEFEF",
        },
        "& .defaulted": {
          color: "green",
        },
        "& .user-input-override": {
          color: "red",
        },
        "& .MuiDataGrid-columnHeader": {
          border: `1px solid ${borderColor}`,
        },
        "& .MuiDataGrid-cell": {
          border: `1px solid ${borderColor}`,
        },
        // overflow must be visible so the sticky panel inside the cell
        // tracks the virtual scroller rather than the (non-scrolling) cell.
        "& .detail-panel-cell": { padding: 2, overflow: "visible" },
        "& .detail-panel-cell:focus, & .detail-panel-cell:focus-within": {
          outline: "none",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        isCellEditable={(params) => !params.row.__detail}
        {...(hasExtraInputs
          ? {
              getRowHeight: (params) =>
                params.model?.__detail ? "auto" : null,
            }
          : {})}
        columnVisibilityModel={{
          expiringSublimits: hasPriorData,
        }}
        onCellEditStart={(params, event) => {
          if (params?.reason === "deleteKeyDown") {
            updateCoverage({
              peril: params?.id,
              field: params?.field,
              value: null,
            });
            event.defaultMuiPrevented = true;
            return;
          }
        }}
        processRowUpdate={(newRow, oldRow) => {
          Object.entries(newRow)
            .filter(([field, value]) => oldRow?.[field] !== value)
            .forEach(([field, value]) => {
              updateCoverage({ peril: newRow.id, field, value });
            });
          return newRow;
        }}
        onProcessRowUpdateError={(error) => logger.error(error)}
        columnGroupingModel={columnGroupingModel}
      />
    </Box>
  );
};

export default CoveragesGrid;
